import { create } from "zustand";
import {
  ENGLISH_LANG_CODE,
  ICategory,
  IGlossary,
  INotificationBaseRelatedToShow,
  IPayloadUpdatedTranslation,
  ITerm,
  NotificationType,
  TranslatableEntity,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { devtools } from "zustand/middleware";
import { IdleManager } from "tools/idle";
import { registerStore } from "store/utils/reset-store.utils";
import { useShallow } from "zustand/react/shallow";

type State = {
  glossaries: Record<string, IGlossary | null>;
  categories: Record<string, ICategory[]>;
  terms: Record<string, ITerm[]>;
  termsLanguage: Record<string, string>;
  categoriesLanguage: Record<string, string>;
  searchByBuildTypeId: Record<string, string | null>;
  popupTermId: Record<string, string | null>;
  searchQuery: string;
  isLoadingGlossary: boolean;
  isLoadingCategories: boolean;
  isLoadingTerms: boolean;
};

type Action = {
  fetch: (showId: string, force?: boolean) => Promise<void>;
  fetchCategories: (
    showId: string,
    force?: boolean,
    languageCode?: string
  ) => Promise<void>;
  fetchTerms: (
    showId: string,
    force?: boolean,
    languageCode?: string
  ) => Promise<void>;
  setSearchByBuildTypeId: (showId: string, buildTypeId: string) => void;
  autoCatch: (showId: string) => Promise<void>;
  getTermsById: (showId: string, id: string) => ITerm | null;
  getAll: () => Record<string, IGlossary | null>;
  setSearchQuery: (query: string) => void;
};

const termsRequestSeq: Record<string, number> = {};
const categoriesRequestSeq: Record<string, number> = {};

/** A read that never answered, as opposed to an answer of "nothing". */
const READ_FAILED: unique symbol = Symbol("glossary read failed");

export const glossaryStore = create(
  devtools<State & Action>((set, get) => ({
    glossaries: {},
    categories: {},
    terms: {},
    termsLanguage: {},
    categoriesLanguage: {},
    searchByBuildTypeId: {},
    popupTermId: {},
    searchQuery: "",
    isLoadingGlossary: false,
    isLoadingCategories: false,
    isLoadingTerms: false,

    getAll: () => {
      const { glossaries } = get();

      return glossaries;
    },
    getTermsById: (showId: string, termId): ITerm | null => {
      const { terms } = get();

      if (!terms?.[showId] || !termId) {
        return null;
      }

      const term = terms[showId].find(({ id }) => id === termId);

      if (!term) {
        return null;
      }

      return term;
    },

    autoCatch: async (showId: string) => {
      const { fetch, fetchCategories, fetchTerms } = get();

      IdleManager.getInstance().addSubscriber(
        `glossary-${showId}`,
        async () => {
          await fetch(showId, true);
          await fetchCategories(showId, true);
          await fetchTerms(showId, true);
        }
      );

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.CREATED_GLOSSARY,
        showId,
        callback: async ({ showId }) => {
          await fetch(showId, true);
          await fetchCategories(showId, true);
          await fetchTerms(showId, true);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.UPDATED_GLOSSARY,
        showId,
        callback: async ({ showId }) => {
          await fetch(showId, true);
          await fetchCategories(showId, true);
          await fetchTerms(showId, true);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.CHANGE_ORDER_CATEGORY_GLOSSARY,
        showId,
        callback: async ({ showId }) => {
          await fetchCategories(showId, true);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.CHANGE_ORDER_TERM_GLOSSARY,
        showId,
        callback: async ({ showId }) => {
          await fetchTerms(showId, true);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.CREATED_CATEGORY_GLOSSARY,
        showId,
        callback: async ({ showId }) => {
          await fetchCategories(showId, true);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.CREATED_TERM_GLOSSARY,
        showId,
        callback: async ({ showId }) => {
          await fetchTerms(showId, true);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.DELETED_CATEGORY_GLOSSARY,
        showId,
        callback: async ({ showId }) => {
          await fetchCategories(showId, true);
          await fetchTerms(showId, true);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.DELETED_TERM_GLOSSARY,
        showId,
        callback: async ({ showId }) => {
          await fetchTerms(showId, true);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.UPDATED_CATEGORY_GLOSSARY,
        showId,
        callback: async ({ showId }) => {
          await fetchCategories(showId, true);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.UPDATED_TERM_GLOSSARY,
        showId,
        callback: async ({ showId }) => {
          await fetchTerms(showId, true);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.DELETE_BUILD_TYPE,
        listenerId: "glossary_to_build_type",
        showId,
        callback: async ({ showId }) => {
          await fetchCategories(showId, true);
          await fetchTerms(showId, true);
        },
      });

      apiV2.notification.subscribe<
        INotificationBaseRelatedToShow<IPayloadUpdatedTranslation>
      >({
        key: NotificationType.UPDATED_TRANSLATION,
        listenerId: "glossary_translation",
        showId,
        callback: async ({ showId: messageShowId, payload }) => {
          if (payload?.entity === TranslatableEntity.TERM) {
            await fetchTerms(messageShowId, true);
            return;
          }

          if (payload?.entity === TranslatableEntity.CATEGORY) {
            await fetchCategories(messageShowId, true);
          }
        },
      });
    },

    setSearchByBuildTypeId: (showId: string, buildTypeId: string) => {
      const { searchByBuildTypeId } = get();

      set(() => ({
        searchByBuildTypeId: {
          ...searchByBuildTypeId,

          [showId]: buildTypeId,
        },
      }));
    },

    fetch: async (showId: string, force = false) => {
      if (!force) {
        const isCached = showId in get().glossaries;
        if (isCached || get().isLoadingGlossary) {
          return;
        }
      }

      set(() => ({ isLoadingGlossary: true }));

      try {
        /*
         * A rejected read must not overwrite a loaded glossary: it used to be
         * caught into `null`, and the page tore its section list down. A
         * genuine `null` — no glossary yet — is still stored.
         */
        const glossary = await apiV2.glossary
          .get({
            showId,
          })
          .catch((): typeof READ_FAILED => READ_FAILED);

        if (glossary === READ_FAILED) {
          return;
        }

        set(() => ({
          glossaries: {
            ...get().glossaries,

            [showId]: glossary,
          },
        }));
      } catch (e) {
        console.error(e);
      } finally {
        set(() => ({ isLoadingGlossary: false }));
      }
    },

    fetchCategories: async (
      showId: string,
      force = false,
      languageCode?: string
    ) => {
      const {
        glossaries,
        categories,
        categoriesLanguage,
        searchByBuildTypeId,
      } = get();

      const nextLanguage =
        languageCode ?? categoriesLanguage[showId] ?? ENGLISH_LANG_CODE;
      const isLanguageChange = categoriesLanguage[showId] !== nextLanguage;

      if (!force && !isLanguageChange) {
        if (get().categories[showId] || get().isLoadingCategories) {
          return;
        }
      }

      categoriesRequestSeq[showId] = (categoriesRequestSeq[showId] ?? 0) + 1;
      const requestId = categoriesRequestSeq[showId];
      const isLatestRequest = () => categoriesRequestSeq[showId] === requestId;

      set(() => ({ isLoadingCategories: true }));

      try {
        const glossary = glossaries[showId];

        if (!glossary) {
          return;
        }

        const categoriesByGlossaryId = await apiV2.glossary.category
          .getAll({
            glossaryId: glossary.id,
            ...(searchByBuildTypeId?.[showId] && {
              buildTypeId: searchByBuildTypeId[showId] as string,
            }),
            ...(nextLanguage !== ENGLISH_LANG_CODE && {
              languageCode: nextLanguage,
            }),
          })
          .catch((): typeof READ_FAILED => READ_FAILED);

        // As above: a failed request must not empty a list already on screen.
        if (categoriesByGlossaryId === READ_FAILED) {
          return;
        }

        if (!isLatestRequest()) {
          return;
        }

        set(() => ({
          categories: {
            ...categories,

            [showId]: categoriesByGlossaryId,
          },
          categoriesLanguage: { ...categoriesLanguage, [showId]: nextLanguage },
        }));
      } catch (e) {
        console.error(e);
      } finally {
        if (isLatestRequest()) {
          set(() => ({ isLoadingCategories: false }));
        }
      }
    },
    fetchTerms: async (
      showId: string,
      force = false,
      languageCode?: string
    ) => {
      const {
        glossaries,
        terms,
        termsLanguage,
        searchByBuildTypeId,
        popupTermId,
      } = get();

      const nextLanguage =
        languageCode ?? termsLanguage[showId] ?? ENGLISH_LANG_CODE;
      const isLanguageChange = termsLanguage[showId] !== nextLanguage;

      if (!force && !isLanguageChange) {
        if (get().terms[showId] || get().isLoadingTerms) {
          return;
        }
      }

      termsRequestSeq[showId] = (termsRequestSeq[showId] ?? 0) + 1;
      const requestId = termsRequestSeq[showId];
      const isLatestRequest = () => termsRequestSeq[showId] === requestId;

      set(() => ({ isLoadingTerms: true }));

      try {
        const glossary = glossaries[showId];

        if (!glossary) {
          return;
        }

        const {
          terms: termsByGlossaryId,
          popupTermId: newPopupTermId,
        } = await apiV2.glossary.category.term.getAll({
          glossaryId: glossary.id,
          ...(searchByBuildTypeId?.[showId] && {
            buildTypeId: searchByBuildTypeId[showId] as string,
          }),
          ...(nextLanguage !== ENGLISH_LANG_CODE && {
            languageCode: nextLanguage,
          }),
        });

        if (!isLatestRequest()) {
          return;
        }

        set(() => ({
          terms: {
            ...terms,

            [showId]: termsByGlossaryId,
          },
          termsLanguage: { ...termsLanguage, [showId]: nextLanguage },
          popupTermId: { ...popupTermId, [showId]: newPopupTermId ?? null },
        }));
      } catch (e) {
        console.error(e);
      } finally {
        if (isLatestRequest()) {
          set(() => ({ isLoadingTerms: false }));
        }
      }
    },
    setSearchQuery: (query) => {
      set({ searchQuery: query });
    },
  }))
);

export const useGlossaries = <R>(selector: (store: State & Action) => R) =>
  glossaryStore(useShallow(selector));

registerStore(glossaryStore);
